// @ts-nocheck — ported from app/web (noUncheckedIndexedAccess)
/**
 * StreamAccumulator: synchronous, total ingestion of decoded stream events
 * into columnar stores + incremental stats. One instance per stream; restart
// and supersede construct a fresh instance.
 *
 * Layout: time (f64), OHLC (f32), volume/equity (f64), per-plot f32
 * columns (meta order). Sparse records (colors, shapes) are event lists.
 * Drawings are a Map keyed by ID. Logs a bounded ring.
 */

import type { Fill, LogEntry, Meta, ShapeEvent, StreamEvent, Trade } from './previewStreamDecoder';

export interface ClosedTrade {
	// Direction of the underlying position ('long' | 'short').
	side: 'long' | 'short';
	entryBarIndex: number;
	entryTime: number;
	entryPrice: number;
	exitBarIndex: number;
	exitTime: number;
	exitPrice: number;
	qty: number;
	pnl: number;
	pnlPercent: number;
	fees: number;
	isOpen: boolean;
}

export interface Stats {
	startEquity: number | null;
	lastEquity: number | null;
	returnPct: number | null;
	maxDrawdown: number;
	maxDrawdownPct: number;
	// Trade stats (from the FIFO lot machine), O(1) counters.
	tradeCount: number;
	winCount: number;
	lossCount: number;
	profitFactor: number | null;
	avgWin: number | null;
	avgLoss: number | null;
	maxConsecWins: number;
	maxConsecLosses: number;
	avgTradeDurationMs: number | null;
}

interface Column {
	time: Float64Array;
	open: Float32Array;
	high: Float32Array;
	low: Float32Array;
	close: Float32Array;
	volume: Float64Array;
	equity: Float64Array;
}

interface SparseColor {
	barIndex: number;
	colors: number[];
}

interface SecurityStore {
	time: Float64Array;
	open: Float32Array;
	high: Float32Array;
	low: Float32Array;
	close: Float32Array;
	volume: Float64Array;
	n: number; // bars written; the typed arrays above are capacity, not length
	gapTimes: number[]; // main-bar times where gaps=on fired
}

const INITIAL_CAP = 4096;

/** Upper bound on kept drawings that Pine's cap dropped (oldest go first). */
const MAX_ARCHIVED_DRAWINGS = 50_000;

export class StreamAccumulator {
	readonly bars: Column = {
		time: new Float64Array(INITIAL_CAP),
		open: new Float32Array(INITIAL_CAP),
		high: new Float32Array(INITIAL_CAP),
		low: new Float32Array(INITIAL_CAP),
		close: new Float32Array(INITIAL_CAP),
		volume: new Float64Array(INITIAL_CAP),
		equity: new Float64Array(INITIAL_CAP)
	};
	nBars = 0;

	meta: Meta | null = null;
	plotColumns: Float32Array[] = [];
	securities: SecurityStore[] = [];

	barColors: SparseColor[] = [];
	bgColors: SparseColor[] = [];
	shapes: ShapeEvent[] = [];
	alerts: { barIndex: number; mask: number }[] = [];
	trades: Trade[] = [];
	fills: Fill[] = [];
	/** A short stream that starts late: its trades and drawings are not the run's, so they are dropped. */
	candlesAndPlotsOnly = false;
	closedTrades: ClosedTrade[] = [];
	drawings = new Map<number, any>();
	/**
	 * Drawings the engine dropped only because Pine keeps the newest N per kind
	 * (max_lines_count / max_boxes_count / max_labels_count). They stay here so
	 * the chart can still show them when the user pans back to that period.
	 */
	archivedDrawings: any[] = [];
	/** Pine's cap per kind, indexed by objKind (0 line, 1 box, 2 label). */
	drawingLimits: number[] = [50, 50, 50];
	/** Bumped on every drawing event; renderers repaint on change. */
	drawingsVersion = 0;
	private liveDrawings: number[] = [0, 0, 0];
	logs: LogEntry[] = [];
	logRingDropped = 0;

	phase: 'historical' | 'live' | 'ended' | 'stale' | 'failed' = 'historical';
	warning: string | null = null;
	errorMessage: string | null = null;

	version = 0; // monotonic ingest counter; renderers batch on this

	// Stats counters (updated on ingest, O(1) per bar).
	private maxDD = 0;
	private maxDDPct = 0;
	private startEquity: number | null = null;
	private lastEquity: number | null = null;
	private peakEquity = 0;
	private grossProfit = 0;
	private grossLoss = 0;
	private nWins = 0;
	private nLosses = 0;
	private nClosed = 0;
	private consecWins = 0;
	private consecLosses = 0;
	private maxConsecWins = 0;
	private maxConsecLosses = 0;
	private sumDurationsMs = 0;

	// FIFO lot queue (mirrors buildClosedTrades / Go closeFifo): one lot per
	// entry fill; pyramiding adds are separate lots, partial closes split the
	// front lot. The engine emits one closed fill per closed portion with the
	// exit fee attached, so portions arrive pre-split.
	private lots: { side: 'long' | 'short'; qty: number; price: number; fee: number; time: number; barIndex: number }[] = [];
	positionOpenLots = 0;

	get position(): { side: 'long' | 'short' | null; qty: number; avgPrice: number } {
		if (this.lots.length === 0) return { side: null, qty: 0, avgPrice: 0 };
		let qty = 0;
		let weighted = 0;
		for (const l of this.lots) {
			qty += l.qty;
			weighted += l.price * l.qty;
		}
		return { side: this.lots[0].side, qty, avgPrice: qty > 0 ? weighted / qty : 0 };
	}

	// Delta clocks.
	private prevMainTime = 0;
	private prevSecTime: number[] = [];

	private logCap = 1000;

	ingest(ev: StreamEvent): void {
		this.version++;
		if (this.candlesAndPlotsOnly && (ev.t === 'trade' || ev.t === 'fill' || ev.t === 'drawing')) return;
		switch (ev.t) {
			case 'meta':
				this.onMeta(ev.meta);
				break;
			case 'bar':
				this.onBar(ev.bar);
				break;
			case 'secBar':
				this.onSecBar(ev.ev);
				break;
			case 'trade':
				this.onTrade(ev.trade);
				break;
			case 'fill':
				this.onFill(ev.fill);
				break;
			case 'drawing':
				this.onDrawing(ev.ev);
				break;
			case 'log':
				this.onLog(ev.entries);
				break;
			case 'shape':
				this.shapes.push(ev.ev);
				break;
			case 'historicalDone':
				this.phase = 'live';
				break;
			case 'done':
				this.phase = ev.error ? 'failed' : 'ended';
				if (ev.warning) this.warning = ev.message;
				else if (ev.error) this.errorMessage = ev.message;
				if (ev.warning && ev.error) {
					this.warning = ev.message;
				}
				break;
		}
	}

	// done frame with both bits: one message; warning surfaces preferentially.
	markStale(): void {
		if (this.phase !== 'ended' && this.phase !== 'failed') this.phase = 'stale';
	}

	private onMeta(meta: Meta): void {
		this.meta = meta;
		this.plotColumns = meta.plots.map(() => new Float32Array(INITIAL_CAP));
		this.securities = meta.securities.map(() => ({
			time: new Float64Array(1024),
			open: new Float32Array(1024),
			high: new Float32Array(1024),
			low: new Float32Array(1024),
			close: new Float32Array(1024),
			volume: new Float64Array(1024),
			n: 0,
			gapTimes: []
		}));
		this.prevSecTime = meta.securities.map(() => 0);
	}

	private ensureCapacity(need: number): void {
		if (need <= this.bars.time.length) return;
		let cap = this.bars.time.length;
		while (cap < need) cap *= 2;
		const grow = (arr: Float64Array) => {
			const nb = new Float64Array(cap);
			nb.set(arr);
			return nb;
		};
		const grow32 = (arr: Float32Array) => {
			const nb = new Float32Array(cap);
			nb.set(arr);
			return nb;
		};
		this.bars.time = grow(this.bars.time);
		this.bars.open = grow32(this.bars.open);
		this.bars.high = grow32(this.bars.high);
		this.bars.low = grow32(this.bars.low);
		this.bars.close = grow32(this.bars.close);
		this.bars.volume = grow(this.bars.volume);
		this.bars.equity = grow(this.bars.equity);
		for (let i = 0; i < this.plotColumns.length; i++) {
			if (this.plotColumns[i].length < cap) {
				const nb = new Float32Array(cap);
				nb.set(this.plotColumns[i]);
				this.plotColumns[i] = nb;
			}
		}
	}

	private onBar(bar: any): void {
		this.ensureCapacity(this.nBars + 1);
		const i = this.nBars++;
		const time = this.prevMainTime + bar.time;
		this.prevMainTime = time;
		this.bars.time[i] = time;
		this.bars.open[i] = bar.open;
		this.bars.high[i] = bar.high;
		this.bars.low[i] = bar.low;
		this.bars.close[i] = bar.close;
		this.bars.volume[i] = bar.volume;
		this.bars.equity[i] = bar.equity;
		if (bar.plots.length > 0) {
			for (let p = 0; p < this.plotColumns.length; p++) {
				this.plotColumns[p][i] = bar.plots[p];
			}
		}
		if (bar.barColors.length > 0) this.barColors.push({ barIndex: i, colors: bar.barColors });
		if (bar.bgColors.length > 0) this.bgColors.push({ barIndex: i, colors: bar.bgColors });
		if (bar.alerts !== 0) this.alerts.push({ barIndex: i, mask: bar.alerts });
		this.updateStats(i, bar.equity, time);
	}

	private onSecBar(ev: any): void {
		if (ev.sec >= this.securities.length) return;
		const store = this.securities[ev.sec];
		if (ev.gap) {
			store.gapTimes.push(this.prevMainTime);
			return;
		}
		const time = this.prevSecTime[ev.sec] + ev.time;
		this.prevSecTime[ev.sec] = time;
		if (store.n === 0 || time !== store.time[store.n - 1]) {
			const i = store.n++;
			const growIf = <T extends Float64Array | Float32Array>(arr: T, ctor: new (n: number) => T): T => {
				if (i >= arr.length) {
					const nb = new ctor(Math.max(arr.length * 2, 1024));
					nb.set(arr);
					return nb;
				}
				return arr;
			};
			store.time = growIf(store.time, Float64Array as any);
			store.open = growIf(store.open, Float32Array as any);
			store.high = growIf(store.high, Float32Array as any);
			store.low = growIf(store.low, Float32Array as any);
			store.close = growIf(store.close, Float32Array as any);
			store.volume = growIf(store.volume, Float64Array as any);
			store.time[i] = time;
			store.open[i] = ev.open;
			store.high[i] = ev.high;
			store.low[i] = ev.low;
			store.close[i] = ev.close;
			store.volume[i] = ev.volume;
		}
	}

	private updateStats(i: number, equity: number, time: number): void {
		if (Number.isNaN(equity)) return; // warmup
		if (this.startEquity === null) this.startEquity = equity;
		this.lastEquity = equity;
		if (equity > this.peakEquity) this.peakEquity = equity;
		const dd = this.peakEquity - equity;
		if (dd > this.maxDD) this.maxDD = dd;
		if (this.peakEquity > 0) {
			const ddPct = (dd / this.peakEquity) * 100;
			if (ddPct > this.maxDDPct) this.maxDDPct = ddPct;
		}
	}

	// Intents are kept raw for display; the position state machine runs on
	// fills (exact price/qty/fee from the engine), not on intents.
	private onTrade(trade: Trade): void {
		this.trades.push(trade);
	}

	// Fill-driven FIFO lot pairing: opened fills push one lot each;
	// closed fills (one per closed portion, exit fee attached) consume
	// lots front-first — exact per-portion P&L from the engine's own fill
	// prices and fees, mirroring Go's closeFifo.
	private onFill(fill: Fill): void {
		this.fills.push(fill);
		const qty = Math.abs(fill.qty);
		if (qty <= 0) return;
		if (fill.kind === 'opened') {
			this.lots.push({
				side: fill.qty > 0 ? 'long' : 'short',
				qty,
				price: fill.price,
				fee: fill.fee,
				time: fill.time,
				barIndex: this.nBars - 1
			});
			this.positionOpenLots = this.lots.length;
			return;
		}
		// Closed fill: reduce the open lots FIFO.
		let remaining = qty;
		while (remaining > 1e-12 && this.lots.length > 0) {
			const lot = this.lots[0];
			const c = Math.min(lot.qty, remaining);
			const proratedEntry = (lot.fee * c) / lot.qty;
			const exitFee = fill.fee * (c / qty);
			const dirSign = lot.side === 'long' ? 1 : -1;
			let pnl = c * (fill.price - lot.price) * dirSign - proratedEntry - exitFee;
			const notional = c * lot.price;
			let pnlPct = 0;
			if (notional !== 0) pnlPct = (pnl / notional) * 100;
			this.closedTrades.push({
				side: lot.side,
				entryBarIndex: lot.barIndex,
				entryTime: lot.time,
				entryPrice: lot.price,
				exitBarIndex: this.nBars - 1,
				exitTime: fill.time,
				exitPrice: fill.price,
				qty: c,
				pnl,
				pnlPercent: pnlPct,
				fees: proratedEntry + exitFee,
				isOpen: false
			});
			this.nClosed++;
			this.sumDurationsMs += fill.time - lot.time;
			if (pnl > 0) {
				this.grossProfit += pnl;
				this.nWins++;
				this.consecWins++;
				if (this.consecWins > this.maxConsecWins) this.maxConsecWins = this.consecWins;
				this.consecLosses = 0;
			} else {
				this.grossLoss += -pnl;
				this.nLosses++;
				this.consecLosses++;
				if (this.consecLosses > this.maxConsecLosses) this.maxConsecLosses = this.consecLosses;
				this.consecWins = 0;
			}
			lot.qty -= c;
			remaining -= c;
			if (lot.qty <= 1e-12) this.lots.shift();
		}
		this.positionOpenLots = this.lots.length;
	}

	// Snapshot of still-open lots priced at the last bar's close — the
	// streaming analogue of EnsureClosedTrades' open-position rows.
	getOpenTrades(lastClose: number, lastTime: number): ClosedTrade[] {
		return this.lots.map((lot) => {
			const dirSign = lot.side === 'long' ? 1 : -1;
			const pnl = lot.qty * (lastClose - lot.price) * dirSign - lot.fee;
			const notional = lot.qty * lot.price;
			return {
				side: lot.side,
				entryBarIndex: lot.barIndex,
				entryTime: lot.time,
				entryPrice: lot.price,
				exitBarIndex: this.nBars - 1,
				exitTime: lastTime,
				exitPrice: lastClose,
				qty: lot.qty,
				pnl,
				pnlPercent: notional !== 0 ? (pnl / notional) * 100 : 0,
				fees: lot.fee,
				isOpen: true
			};
		});
	}

	private onDrawing(ev: any): void {
		this.drawingsVersion++;
		if (ev.eventKind !== 2) {
			if (!this.drawings.has(ev.id)) this.liveDrawings[ev.objKind] = (this.liveDrawings[ev.objKind] || 0) + 1;
			this.drawings.set(ev.id, ev);
			return;
		}
		const prev = this.drawings.get(ev.id);
		if (!prev) return;
		const kind = prev.objKind;
		// The wire has one delete event for both cases. It is Pine's cap (not the
		// script's own delete) when the kind is full and its oldest object goes.
		if (this.liveDrawings[kind] >= (this.drawingLimits[kind] ?? Infinity) && this.isOldestOfKind(ev.id, kind)) {
			if (this.archivedDrawings.length >= MAX_ARCHIVED_DRAWINGS) this.archivedDrawings.shift();
			this.archivedDrawings.push(prev);
		}
		this.liveDrawings[kind]--;
		this.drawings.delete(ev.id);
	}

	/** Map order is creation order (ids never repeat; updates keep their slot). */
	private isOldestOfKind(id: number, kind: number): boolean {
		for (const d of this.drawings.values()) {
			if (d.objKind === kind) return d.id === id;
		}
		return false;
	}

	private onLog(entries: LogEntry[]): void {
		for (const ent of entries) {
			if (this.logs.length === this.logCap) {
				this.logs.shift();
				this.logRingDropped++;
			}
			this.logs.push(ent);
		}
	}

	getStats(): Stats {
		return {
			startEquity: this.startEquity,
			lastEquity: this.lastEquity,
			returnPct: this.startEquity && this.startEquity !== 0 && this.lastEquity !== null ? ((this.lastEquity - this.startEquity) / this.startEquity) * 100 : null,
			maxDrawdown: this.maxDD,
			maxDrawdownPct: this.maxDDPct,
			tradeCount: this.nClosed,
			winCount: this.nWins,
			lossCount: this.nLosses,
			profitFactor: this.grossLoss > 0 ? this.grossProfit / this.grossLoss : null,
			avgWin: this.nWins > 0 ? this.grossProfit / this.nWins : null,
			avgLoss: this.nLosses > 0 ? this.grossLoss / this.nLosses : null,
			maxConsecWins: this.maxConsecWins,
			maxConsecLosses: this.maxConsecLosses,
			avgTradeDurationMs: this.nClosed > 0 ? this.sumDurationsMs / this.nClosed : null
		};
	}

	// getClosedTradesView returns closed trades plus a synthesized open row
	// per still-open lot (is_open semantics, matching the preview page).
	getClosedTradesView(): ClosedTrade[] {
		if (this.lots.length === 0) return this.closedTrades;
		const lastClose = this.nBars > 0 ? this.bars.close[this.nBars - 1] : NaN;
		const lastTime = this.nBars > 0 ? this.bars.time[this.nBars - 1] : 0;
		return this.closedTrades.concat(this.getOpenTrades(lastClose, lastTime));
	}
}
